import type { Cue, ClusterDetail, RepPromptContent, StudyRow } from '../types/snak'

export const snakMeta = {
  congress: 'NOK 2026',
  kicker: 'Rep-guided conversation',
  principle: 'Your view first. Then the data.',
  peerLabel: 'of NOK delegates answered the same',
}

export const permission = {
  eyebrow: 'One question',
  headline: 'Can I get your take on one OCT question?',
  support: '30 seconds. Your view first, then the data.',
  cta: 'Start',
}

export const expertQuestion = {
  cue: {
    question: 'When two DME patients look different on OCT, what matters most to you?',
    options: [
      { id: 'fluid', label: 'Fluid pattern' },
      { id: 'structure', label: 'Retinal structure' },
      { id: 'combination', label: 'The overall combination' },
    ],
  } as Cue,
  note: 'No right or wrong answer — this is your read.',
  cta: 'See why we’re asking',
}

export const studyBridge = {
  headline: 'That variation is exactly what a recent analysis explored.',
  copy: 'An AI-assisted analysis of 2,355 DME eyes examined five familiar OCT variables together.',
  variables: ['IRF', 'SRF', 'I-HRF', 'ELM', 'EZ'],
  question:
    'Do recurring morphological patterns emerge when these features are analysed together?',
  cta: 'See what emerged',
}

export const clusterReveal = {
  eyebrow: 'What the analysis found',
  headline: 'Four distinct morphological clusters emerged.',
  hint: 'Tap any scan to enlarge.',
  cta: 'Continue',
}

export const reflection = {
  cue: {
    question: 'Do these patterns resemble the heterogeneity you see in practice?',
    options: [
      { id: 'often', label: 'Often', share: 61 },
      { id: 'sometimes', label: 'Sometimes', share: 31 },
      { id: 'not', label: 'Not really', share: 8 },
    ],
  } as Cue,
  cta: 'What does the study actually show?',
}

export const evidence = {
  headline: 'What this does — and does not — tell us',
  shows: {
    label: 'What it shows',
    copy: 'Familiar OCT variables formed recurring morphological clusters when analysed together across a large DME cohort.',
  },
  doesNot: {
    label: 'What it does not show',
    copy: 'The clusters are not validated treatment-selection categories.',
  },
  support:
    'The study is exploratory and morphological. Further validation is required before the framework can be used to guide treatment decisions.',
  cta: 'Continue',
}

export const relevance = {
  headline: ['The AI helped identify the pattern.', 'The features are already in the OCT.'],
  copy: 'The analysis used AI-assisted quantification, but the variables themselves are familiar features already visible in routine OCT.',
  cue: {
    question: 'Could a more systematic way of looking at those combinations be useful?',
    options: [
      { id: 'potentially', label: 'Potentially' },
      { id: 'already', label: 'Already useful' },
      { id: 'not-yet', label: 'Not yet convinced' },
    ],
  } as Cue,
  cta: 'Explore further',
}

export const chooseDepth = {
  headline: 'Where would you like to go next?',
  support: 'You decide how deep we go.',
  options: [
    {
      id: 'clusters' as const,
      label: 'Compare the 4 clusters',
      description: 'Open a deeper cluster-comparison view.',
    },
    {
      id: 'study' as const,
      label: 'Study design',
      description: 'Open methodology and limitations.',
    },
    {
      id: 'end' as const,
      label: 'That’s enough for now',
      description: 'End the interaction.',
    },
  ],
}

export const endPage = {
  headline: 'One diagnosis. Different morphological patterns.',
  support:
    'The question is whether recognising those patterns more systematically could add useful context to the way DME is characterised.',
  close: 'Thank you for your perspective.',
}

export const clusterDetails: ClusterDetail[] = [
  {
    id: 'c1',
    descriptors: ['Mainly central IRF', 'No SRF', 'ELM / EZ intact', 'Fewer I-HRF'],
  },
  {
    id: 'c2',
    descriptors: ['Mainly peripheral IRF', 'No SRF', 'ELM / EZ intact', 'More I-HRF'],
  },
  {
    id: 'c3',
    descriptors: ['Central + peripheral IRF', 'SRF present', 'EZ disruption', 'Higher I-HRF'],
  },
  {
    id: 'c4',
    descriptors: [
      'Central + peripheral IRF',
      'Greater SRF',
      'ELM + EZ disruption',
      'Higher I-HRF',
    ],
  },
]

export const compareView = {
  eyebrow: 'Deeper view',
  headline: 'Compare the four morphological clusters',
  note: 'Morphological descriptors only. No progression, prognosis or treatment implication.',
  cta: 'Back',
}

export const studyDesign = {
  eyebrow: 'Study design',
  headline: 'Methodology and limitations',
  rows: [
    { label: 'Population', value: '2,355 DME eyes / 1,688 patients' },
    { label: 'Design', value: 'Retrospective, cross-sectional, real-world analysis' },
    { label: 'Imaging', value: 'OCT biomarker analysis' },
    { label: 'Variables', value: 'IRF, SRF, I-HRF, ELM, EZ' },
    { label: 'Method', value: 'Automated biomarker quantification + model-based clustering' },
    { label: 'Result', value: 'Four morphological clusters' },
  ] as StudyRow[],
  limitationsLabel: 'Important limitations',
  limitations: [
    'Cross-sectional and exploratory',
    'Treatment history was not analysed as a treatment-selection framework',
    'The clusters are not validated treatment-selection categories',
    'Longitudinal validation is required',
  ],
  cta: 'Return to conversation',
}

export const chapters: Record<string, string> = {
  permission: 'Permission',
  question: 'Your view',
  bridge: 'The analysis',
  reveal: 'What emerged',
  reflection: 'Your read',
  evidence: 'Credibility',
  relevance: 'Relevance',
  depth: 'Your choice',
  end: 'Close',
}

export const repPrompts: Record<string, RepPromptContent> = {
  permission: {
    say: ['“I’m interested in how you see this — then I’ll show you why the question came up.”'],
    avoid: 'Don’t open by explaining the study.',
  },
  question: {
    say: ['“What makes you say that?”'],
    avoid: 'No right/wrong feedback. Let the ophthalmologist speak first.',
  },
  bridge: {
    say: ['“Have you seen this study before?”'],
    avoid: 'Keep it short. Don’t define the OCT variables.',
  },
  reveal: {
    say: ['“Do these look like patterns you recognise from your own patients?”'],
    avoid: 'Don’t explain the clusters first — let them interpret.',
  },
  reflection: {
    say: [
      '“Which of the patterns feels most familiar to you?”',
      '“Is there anything here you would interpret differently?”',
    ],
  },
  evidence: {
    say: [
      '“Exactly — it’s not a treatment algorithm. The interesting part is whether familiar morphology can be described more systematically.”',
    ],
  },
  relevance: {
    say: ['“What would it need to do to become useful for you?”'],
    avoid: 'Don’t claim AI-enabled phenotyping is ready for routine Nordic practice.',
  },
  depth: {
    say: ['“How deep would you like to go?”'],
  },
  end: {
    say: ['“Thank you — your read on this is the useful part.”'],
  },
}

export const snakReference =
  'Midena E, et al. AI-assisted OCT biomarker analysis, 2,355 DME eyes / 1,688 patients. Exploratory, cross-sectional analysis.'

export const snakDisclaimer =
  'Non-promotional scientific exchange. The clusters are morphological patterns — not treatment groups, treatment-response categories or validated treatment-selection criteria.'
